import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate } from "@/lib/format";
import { fileUrl } from "@/lib/upload";
import { Loader, PageTitle, StatusBadge } from "@/components/common";
import { DataView } from "@/components/console/DataView";
import { statusInfo } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdmissionSummary } from "@/components/member/NetworkAI";

const LABELS = { company: "Entreprise", city: "Ville", country: "Pays", legal_status: "Statut juridique", years_in_business: "Années d'activité", sectors: "Secteurs",
  product_categories: "Catégories", trade_mode: "Gros / détail", importing: "Importation", sourcing_countries: "Pays d'approvisionnement", brings: "J'apporte", seeks: "Je cherche", bio: "Présentation", languages: "Langues" };

const ENUMS = { gros: "En gros", detail: "Au détail", gros_detail: "En gros et au détail", actuelle: "J'importe déjà", envisagee: "J'envisage d'importer", non: "Non" };
const show = (v) => (Array.isArray(v) ? v.join(", ") : ENUMS[v] || String(v ?? "—"));

function Detail({ id, onClose, onDone }) {
  const [a, setA] = useState(null);
  const [grid, setGrid] = useState({});
  const [reason, setReason] = useState("");
  useEffect(() => {
    api.get(`/admin/applications/${id}`).then(({ data }) => { setA(data); setGrid(data.grid || {}); });
  }, [id]);
  const decide = async (action) => {
    try {
      await api.post(`/admin/applications/${id}/decision`, { action, reason, grid });
      toast.success("Décision enregistrée");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const openFile = async (fid) => window.open(await fileUrl(fid), "_blank", "noopener");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="application-detail">
        <DialogHeader><DialogTitle>{a ? a.candidate.name : "Dossier"}</DialogTitle></DialogHeader>
        {!a ? <Loader /> : (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap gap-2 items-center"><StatusBadge status={a.status} testId="application-detail-status" /><span className="text-mr-muted">{a.candidate.email} · {a.candidate.phone}</span></div>
            <div className="grid sm:grid-cols-2 gap-3">{Object.entries(LABELS).map(([k, l]) => (
              <div key={k}><p className="text-xs text-mr-muted">{l}</p><p>{show(a.dossier[k])}</p></div>))}</div>
            <div className="flex gap-2">
              {a.photo_file_id && <button className="mr-btn-ghost px-3" onClick={() => openFile(a.photo_file_id)} data-testid="application-photo-button">Photo</button>}
              {a.id_document_file_id && <button className="mr-btn-ghost px-3" onClick={() => openFile(a.id_document_file_id)} data-testid="application-id-button">Pièce d'identité (chiffrée)</button>}
            </div>
            <div className="border border-mr-border rounded-[3px] p-3" data-testid="application-sponsor-opinion">
              <p className="text-xs text-mr-muted">Avis du parrain {a.sponsor_name && `(${a.sponsor_name})`}</p>
              <p>{a.sponsor_knows === null || a.sponsor_knows === undefined ? (a.sponsor_id ? "En attente" : "Invitation du bureau") : `${a.sponsor_knows ? "Connaît le candidat" : "Ne connaît pas le candidat"} — ${a.sponsor_opinion || "sans note"}`}</p>
            </div>
            <AdmissionSummary appId={id} />
            <div className="space-y-2" data-testid="application-grid"><p className="font-medium">Grille d'évaluation</p>
              {a.grid_criteria.map((c) => (
                <label key={c} className="flex items-center justify-between gap-3"><span>{c}</span>
                  <select className="mr-input w-24" value={grid[c] || ""} onChange={(e) => setGrid({ ...grid, [c]: e.target.value })} data-testid={`grid-${c}`}><option value="">—</option>{[1, 2, 3, 4, 5].map((n) => <option key={n}>{n}</option>)}</select></label>))}
            </div>
            <textarea data-testid="decision-reason-input" className="mr-input min-h-[80px]" placeholder="Motif du refus ou informations demandées" value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button data-testid="decision-approve-button" className="mr-btn-primary" onClick={() => decide("approve")}>Approuver</button>
              <button data-testid="decision-info-button" className="mr-btn-secondary" onClick={() => decide("request_info")}>Demander des infos</button>
              <button data-testid="decision-hold-button" className="mr-btn-secondary" onClick={() => decide("hold")}>Mettre en attente</button>
              <button data-testid="decision-refuse-button" className="mr-btn-danger" onClick={() => decide("refuse")}>Refuser</button>
            </div>
            {a.history?.length > 0 && <ul className="text-xs text-mr-muted space-y-1">{a.history.map((h, k) => <li key={k}>{fdate(h.at)} · {h.event}{h.reason ? ` · ${h.reason}` : ""}</li>)}</ul>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function Admissions() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/admissions", { params: { status } }).then(({ data: d }) => setData(d));
  useEffect(() => {
    load();
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const prospect = async (p, invite) => {
    try {
      if (invite) {
        const { data: inv } = await api.post(`/admin/prospects/${p.id}/invite`);
        toast.success(`Invitation ${inv.code} envoyée à ${p.email}`);
      } else {
        await api.post(`/admin/prospects/${p.id}/decline`, { reason: window.prompt("Motif (interne)") || "" });
        toast.success("Demande déclinée");
      }
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!data) return <Loader />;
  return (
    <div className="space-y-6">
      <PageTitle eyebrow="Admission" title="File d'admission" />
      <div className="flex flex-wrap gap-2">{[["", "Toutes"], ...Object.keys(data.counts).map((k) => [k, k])].map(([k]) => (
        <button key={k || "all"} data-testid={`admission-filter-${k || "all"}`} onClick={() => setStatus(k)} className={`px-3 h-8 rounded-full border text-xs ${status === k ? "border-mr-accent text-mr-accent" : "border-mr-border text-mr-text2"}`}>
          {k ? <StatusBadge status={k} /> : "Toutes"} {k ? data.counts[k] : ""}</button>))}</div>
      <DataView page="admissions" title="File d'admission" rows={data.items} empty="Aucun dossier" onRowClick={(a) => setOpen(a.id)}
        columns={[{ key: "candidate_name", label: "Candidat", className: "font-medium" }, { key: "company", label: "Entreprise", value: (a) => a.dossier.company || "" }, { key: "sponsor_name", label: "Parrain", value: (a) => a.sponsor_name || "bureau" },
          { key: "candidate_phone", label: "Téléphone", hidden: true }, { key: "candidate_email", label: "E-mail", hidden: true },
          { key: "status", label: "Statut", value: (a) => statusInfo(a.status)[0], render: (a) => <StatusBadge status={a.status} /> }]}
        filters={[{ key: "sponsor_name", label: "Parrain", get: (a) => a.sponsor_name || "bureau", options: [...new Set(data.items.map((a) => a.sponsor_name || "bureau"))].map((s) => [s, s]) }]}
        kanban={{ get: (a) => a.status, columns: Object.keys(data.counts).map((k) => [k, statusInfo(k)[0]]) }}
        kpis={[{ key: "total", label: "Dossiers", value: (r) => r.length }, { key: "study", label: "À étudier", value: (r) => r.filter((a) => ["soumis", "en_etude", "avis_parrain"].includes(a.status)).length },
          { key: "approved", label: "Approuvés", value: (r) => r.filter((a) => a.status === "approuve").length }, { key: "drafts", label: "Brouillons candidats", value: () => data.drafts }]} />
      <section className="space-y-2" data-testid="prospects-section">
        <p className="font-medium">Demandes du site public ({data.prospects.length})</p>
        {data.prospects.map((p) => (
          <div key={p.id} className="mr-card p-4 space-y-2" data-testid="prospect-row">
            <div className="flex justify-between gap-3"><p className="font-medium">{p.name} <span className="text-mr-muted font-normal">· {p.company} · {p.city}</span></p><StatusBadge status={p.status} /></div>
            <p className="text-sm text-mr-text2">{p.brings}</p>
            <div className="flex gap-2"><button data-testid="prospect-invite-button" className="mr-btn-primary px-3" onClick={() => prospect(p, true)} disabled={!p.email}>Inviter par e-mail</button>
              <button data-testid="prospect-decline-button" className="mr-btn-ghost px-3" onClick={() => prospect(p, false)}>Décliner</button></div>
          </div>))}
      </section>
      {open && <Detail id={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />}
    </div>
  );
}

export function SponsorGraph() {
  const [g, setG] = useState(null);
  useEffect(() => {
    api.get("/admin/sponsorship-graph").then(({ data }) => setG(data));
  }, []);
  const tree = useMemo(() => {
    if (!g) return null;
    const kids = {};
    g.edges.forEach((e) => { (kids[e.from] = kids[e.from] || []).push(e.to); });
    const byId = Object.fromEntries(g.nodes.map((n) => [n.id, n]));
    const roots = g.nodes.filter((n) => !g.edges.some((e) => e.to === n.id));
    return { kids, byId, roots };
  }, [g]);
  if (!tree) return <Loader />;
  const Node = ({ id, depth }) => {
    const n = tree.byId[id];
    if (!n || depth > 8) return null;
    return (
      <li className="mt-1">
        <div className="flex items-center gap-2 text-sm" data-testid="graph-node"><span className="w-2 h-2 rounded-full bg-mr-accent" />{n.name}<StatusBadge status={n.status} />{tree.kids[id] && <span className="text-xs text-mr-muted">{tree.kids[id].length} filleul(s)</span>}</div>
        {tree.kids[id] && <ul className="ml-5 border-l border-mr-border pl-3">{tree.kids[id].map((k) => <Node key={k} id={k} depth={depth + 1} />)}</ul>}
      </li>
    );
  };
  return (
    <div className="space-y-4">
      <PageTitle eyebrow="Admission" title="Graphe de parrainage" />
      <p className="text-sm text-mr-text2">Qui a invité qui. Aucun paiement n'est lié au parrainage.</p>
      <ul className="mr-card" data-testid="sponsor-graph">{tree.roots.map((r) => <Node key={r.id} id={r.id} depth={0} />)}</ul>
    </div>
  );
}
